import React from 'react';
import PropTypes from 'prop-types';
import { LuFile, LuFileImage, LuFileSpreadsheet, LuFileText, LuTrash2, LuUpload } from 'react-icons/lu';
import IconButton from '../../../components/ui/IconButton';
import useBudgetText from '../budgetText';

/** Titled group of form fields. */
export const FormSection = ({ icon: Icon, title, children }) => (
  <section className="tw-space-y-3">
    <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
      <span className="tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-lg tw-bg-slate-900/[0.05] tw-text-slate-500 dark:tw-bg-white/[0.07] dark:tw-text-slate-300">
        <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
      </span>
      {title}
    </h3>
    {children}
  </section>
);

FormSection.propTypes = { icon: PropTypes.elementType.isRequired, title: PropTypes.string.isRequired, children: PropTypes.node };

/** Icon for a file by its extension. */
export const fileIconFor = (fileName) => {
  const ext = String(fileName || '').split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return LuFileImage;
  if (ext === 'pdf' || ['doc', 'docx'].includes(ext)) return LuFileText;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return LuFileSpreadsheet;
  return LuFile;
};

/**
 * Drag-and-drop area that also opens the file picker (click or Enter / Space).
 * The handlers are the dialog's own (drag over / leave / drop, input change).
 */
export const FileDropZone = ({ title, hint, isDragging, inputRef, accept, multiple, onDragOver, onDragLeave, onDrop, onInputChange, icon: Icon = LuUpload }) => (
  <div
    role="button"
    tabIndex={0}
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    onClick={() => inputRef.current.click()}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        inputRef.current.click();
      }
    }}
    className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border-2 tw-border-dashed tw-px-4 tw-py-8 tw-text-center tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${isDragging ? 'tw-border-teal-500 tw-bg-teal-500/[0.07]' : 'tw-border-slate-300 tw-bg-slate-50/60 hover:tw-border-teal-400 hover:tw-bg-teal-500/[0.04] dark:tw-border-white/15 dark:tw-bg-white/[0.02] dark:hover:tw-border-teal-400/60'}`}
  >
    <span className="tw-grid tw-h-11 tw-w-11 tw-place-items-center tw-rounded-xl tw-bg-teal-500/10 tw-text-teal-600 dark:tw-text-teal-300">
      <Icon aria-hidden="true" className="tw-h-5 tw-w-5" />
    </span>
    <span className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{title}</span>
    <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{hint}</span>
    <input ref={inputRef} type="file" multiple={multiple} accept={accept} onChange={onInputChange} className="tw-hidden" />
  </div>
);

FileDropZone.propTypes = {
  title: PropTypes.string.isRequired,
  hint: PropTypes.string,
  isDragging: PropTypes.bool,
  inputRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  onDragOver: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  icon: PropTypes.elementType,
};

/** One chosen file with its size and a remove button. */
export const FileRow = ({ name, size, onRemove, disabled, accent }) => {
  const { label } = useBudgetText();
  const Icon = fileIconFor(name);
  return (
    <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-px-3 tw-py-2 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
      <span className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-lg ${accent || 'tw-bg-slate-900/[0.05] tw-text-slate-500 dark:tw-bg-white/[0.07] dark:tw-text-slate-300'}`}>
        <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-block tw-truncate tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{name}</span>
        <span className="tw-block tw-text-xs tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{(size / 1024).toFixed(1)} KB</span>
      </span>
      <IconButton label={label('form.removeFile')} tone="alert" onClick={onRemove} disabled={disabled} className="tw-shrink-0">
        <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
      </IconButton>
    </div>
  );
};

FileRow.propTypes = { name: PropTypes.string.isRequired, size: PropTypes.number, onRemove: PropTypes.func.isRequired, disabled: PropTypes.bool, accent: PropTypes.string };
